import Actuator from './Actuator';
import Parseable from '@/models/Parseable';
import ContactPerson from '@/models/ContactPerson';
import { isRevisionStand, padRevisionStand, revisionStandIndex, RevisionStand } from '@/constants/revisionStands';

/** Selection id for actuators without any revision (legacy data); matches the backend `?revision=none`. */
export const WORKING_LIST_ID = 'none';

/** Stand of a revision: stored `stand` first, otherwise the "-NN" name suffix for Altdaten. */
export function revisionStandOf(revision?: Partial<Revision> | null): RevisionStand | null {
    const stand = revision?.stand;
    if (isRevisionStand(stand)) {
        return stand;
    }
    const match = /-(\d+)$/.exec(revision?.name || '');
    return match ? padRevisionStand(parseInt(match[1], 10)) : null;
}

/** Numeric index of the stand ("00" = 0). Null for legacy revisions without stand or name suffix. */
export function revisionIndex(revision?: Partial<Revision> | null): number | null {
    const stand = revisionStandOf(revision);
    if (!stand) {
        return null;
    }
    const index = revisionStandIndex(stand);
    return index < 0 ? null : index;
}

/** Number of changes requested by the customer, i.e. all revisions except the original "-00". */
export function countChanges(revisions?: Partial<Revision>[] | null): number {
    const sorted = [...(revisions || [])].sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
    return sorted.filter((revision, position) => {
        const index = revisionIndex(revision);
        return index === null ? position > 0 : index > 0;
    }).length;
}

/** Current revision of a project: `isLatest`, otherwise highest stand / newest. */
export function latestRevision(revisions?: Partial<Revision>[] | null): Partial<Revision> | undefined {
    const list = revisions || [];
    if (!list.length) {
        return undefined;
    }
    const marked = list.find((revision) => revision.isLatest);
    if (marked) {
        return marked;
    }
    return [...list].sort((a, b) => {
        const indexDiff = (revisionIndex(b) ?? -1) - (revisionIndex(a) ?? -1);
        if (indexDiff !== 0) {
            return indexDiff;
        }
        return (b.createdAt || 0) - (a.createdAt || 0);
    })[0];
}

/** Label for the project overview: the stand itself ("00", "01"), like the Excel REVISION column. */
export function revisionStandLabel(revisions?: Partial<Revision>[] | null): string {
    return revisionStandOf(latestRevision(revisions)) || '';
}

export default class Revision extends Parseable {
    // Unique identifier for the revision
    id?: string;

    // Name of the revision (e.g., "Initial Version", "Update Q1 2024")
    title?: string;

    // Generated by the backend: "<angebotsNr>-NN"; the stand itself lives in `stand`
    name?: string;

    // Eigenständiger Revisionsstand "00"…"99" (nicht aus dem Namen ableiten)
    stand?: RevisionStand | null;

    createdAt?: number;

    // Indicates whether this is the latest revision
    isLatest!: boolean;

    // Reference to the project ID this revision belongs to
    project!: string;

    // Reference to the previous revision ID (if any)
    previousVersion?: string;

    // Actuators linked to this revision (array of actuator IDs or objects)
    public actuators?: Actuator[] = [];

    // Legacy only: the stage now lives on the project (Project.stage)
    tag?: 'feet' | 'budget' | 'purchase' | '';

    comment?: string;
    contactPerson?: ContactPerson | string;
    creator?: string;

    public static parseFromObject(object: Partial<Revision>): Revision {
        const revision = new Revision();
        Object.assign(revision, object);
        if (object.actuators) {
            revision.actuators = Actuator.parseFromArray(object.actuators!);
        }
        if (typeof object.contactPerson === 'object' && object.contactPerson !== null) {
            revision.contactPerson = ContactPerson.parseFromObject(object.contactPerson);
        }
        return revision;
    }

    public static parseFromArray(array: Partial<Revision>[]): Revision[] {
        if (!Array.isArray(array)) {
            return [];
        }

        return array.map((obj) => Revision.parseFromObject(obj));
    }

    parseToObject(): any {
        return { ...this };
    }
}
